/**
 * 신규설치 검토 산출용 플레이스홀더 로직.
 * 법규·외부 데이터 연동 시 이 모듈의 계산·문구 생성부만 교체하면 됨.
 */

export type FacilityType = 'housing' | 'public' | 'commercial' | 'office' | 'other';
export type BuildStage = 'plan' | 'permit' | 'design' | 'preconstruction' | 'other';
export type FastNeed = 'none' | 'review' | 'needed' | 'unknown';

export interface NewInstallInput {
  facilityType: FacilityType;
  totalParking: number;
  buildStage: BuildStage;
  plannedChargers?: number;
  fastNeed: FastNeed;
}

export interface NewInstallResult {
  guidelineReviewUnits: number;
  reviewSummary: string;
  checkpoints: string[];
  planComparisonHint: string;
}

const RATE: Record<FacilityType, number> = {
  housing: 0.05,
  public: 0.035,
  commercial: 0.04,
  office: 0.038,
  other: 0.04,
};

const STAGE_WEIGHT: Record<BuildStage, number> = {
  plan: 0.9,
  permit: 0.95,
  design: 1,
  preconstruction: 1.05,
  other: 1,
};

export function computeNewInstallResult(input: NewInstallInput): NewInstallResult {
  const base = Math.max(1, Math.ceil(input.totalParking * RATE[input.facilityType]));
  const weighted = Math.max(1, Math.ceil(base * STAGE_WEIGHT[input.buildStage]));
  let guidelineReviewUnits = weighted;

  if (input.fastNeed === 'needed') guidelineReviewUnits = Math.max(guidelineReviewUnits, Math.ceil(weighted * 1.15));
  if (input.fastNeed === 'review') guidelineReviewUnits = Math.max(guidelineReviewUnits, weighted);

  const planned = input.plannedChargers;
  let planComparisonHint =
    planned === undefined || Number.isNaN(planned)
      ? '계획 중인 충전기 대수를 입력하시면 이번 산출과 비교해 볼 수 있습니다.'
      : planned >= guidelineReviewUnits
        ? '입력하신 계획 대수는 이번 검토 기준과 같거나 그 이상입니다. 전력·동선·운영 조건을 함께 점검하는 것이 좋습니다.'
        : '입력하신 계획 대수가 이번 검토 기준보다 적을 수 있습니다. 확대·분산 설치 여부를 검토해 보세요.';

  const reviewSummary =
    `입력하신 시설 유형·주차면·건축 단계를 반영한 검토 기준 대수는 ${guidelineReviewUnits}대입니다. ` +
    `관계 법령·지자체 조례·현장 조건에 따라 실제 적용은 달라질 수 있습니다.`;

  const checkpoints = [
    '주차장 전력 용량·수전 위치와 분전반 여유',
    '완속·급속 배치와 동선·장애인 주차 구역 규정',
    '추가 증설 시 트렌치·통신·운영 시스템 연계',
    '운영 주체(관리사무소·위탁) 및 과금 정책 정리',
  ];

  return {
    guidelineReviewUnits,
    reviewSummary,
    checkpoints,
    planComparisonHint,
  };
}

/** 자동 조회 UI용 안내 문구 (시나리오는 내부 전용) */
export type AutofillScenario = 'success' | 'notFound';

export function autofillNotice(scenario: AutofillScenario): { variant: 'success' | 'danger'; title: string; body: string } {
  if (scenario === 'success') {
    return {
      variant: 'success',
      title: '조회 결과',
      body: '연계 가능한 정보가 확인되었습니다. 주차면 수는 현장 기준으로 수정할 수 있습니다.',
    };
  }
  return {
    variant: 'danger',
    title: '자동 조회',
    body: '일치하는 등록 정보를 찾지 못했습니다. 주차면 수를 직접 입력해 주세요.',
  };
}
